import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import {
  MyWinesShortlistDocument,
  MyWinesDocument,
  ProductsShortlistFilterDocument,
  ProductsOrWinesFiltersDocument,
  ShortlistFiltersDocument,
  ProductsOrWinesFilterDocument,
} from 'api/hooks'
import { getFilterVariables } from 'api/myWines/wines'
import { getSelectedCompanyId } from 'utils/company'
import { ls } from 'utils/my-localstorage'
import { reset, setItems, setPageInfo, getPageInfo } from 'store/my-wines'
import { reset as databaseReset } from 'store/database'
import {
  CHECKBOX_LIST,
  DATE_INTERVAL,
  filterTypes,
  INTERVAL,
  RADIO_LIST,
  RHEOSTAT,
  RRP_INTERVAL,
  SCORE,
  SEARCH_USERS,
  SWITCHER,
  MULTISELECT_SWITCHER,
} from 'utils/consts'
import { getMyWinesFiltersCollection } from 'utils/filtersCollections'
import { getLocaleId } from 'utils/getLocaleId'
import { useFilterStoreData } from 'utils/hooks/useFilterStoreData'
import {
  useCalculatePageOnPerPageChange,
  usePerPage,
} from 'components/Common/Table/utils'
import { getSelectedCompany } from 'store/me'
import { useAppSelector } from 'store/hooks'

const STATUS_APPROVED = 'approved'
const STATUS_VERIFIED = 'verified'
const STATUS_UNAPPROVED = 'unapproved'
export const STATUS_INVALID = 'invalid'

const DEFAULT_SORT = 'UPDATED_AT_DESC'

const getVariables = ({ page = 1, perPage = 20, query, facetsInputs = [] }) => {
  return {
    localeId: getLocaleId(),
    companyId: getSelectedCompanyId(),
    page,
    perPage,
    query,
    ...(query && { query }),
    filter: {
      ...(facetsInputs && facetsInputs.length > 0 && { facetsInputs }),
      onlyAccepted: false,
    },
    activeOfferStatusIds: [1],
  }
}
const useGetVariables = () => {
  const { value: perPage } = usePerPage()
  return variables => getVariables({ ...variables, perPage })
}

export const determineStatus = ({
  shouldOverrideAcceptability,
  verified,
} = {}) => {
  return (
    (verified && STATUS_VERIFIED) ||
    (shouldOverrideAcceptability && STATUS_UNAPPROVED) ||
    (!shouldOverrideAcceptability && STATUS_APPROVED)
  )
}

const mapFilterForQuery = (type, value, apiFilterName) => {
  if (!value) return null

  if (type === filterTypes[CHECKBOX_LIST]) {
    if (value.length === 0 || value === -1) return null
    return {
      listInput: {
        key: apiFilterName,
        byName: value,
      },
    }
  }

  if (type === filterTypes[SEARCH_USERS]) {
    if (value.length === 0) return null
    return {
      freeListInput: {
        key: apiFilterName,
        byName: value,
      },
    }
  }

  if (type === filterTypes[DATE_INTERVAL]) {
    return {
      dateRangeInput: {
        key: apiFilterName,
        fromDate: value?.fromDate ?? null,
        toDate: value?.toDate ?? null,
      },
    }
  }

  if (type === filterTypes[MULTISELECT_SWITCHER]) {
    if (value?.list?.length > 0 || value?.switcher) {
      return {
        complexInput: {
          key: 'grapeAndMonocepage',
          childrenInputs: [
            {
              booleanInput: {
                key: 'grapeAndMonocepage.monocepage',
                value: value?.switcher,
              },
            },
            {
              listInput: {
                key: 'grapeAndMonocepage.grape',
                byName: value?.list,
              },
            },
          ],
        },
      }
    }

    return {
      dateRangeInput: {
        key: apiFilterName,
        fromDate: value?.fromDate ?? null,
        toDate: value?.toDate ?? null,
      },
    }
  }

  if (type === filterTypes[RADIO_LIST]) {
    return {
      listInput: {
        key: apiFilterName,
        byName: value,
      },
    }
  }

  if (type === filterTypes[RHEOSTAT] || type === filterTypes[INTERVAL]) {
    return {
      rangeInput: {
        key: apiFilterName,
        ...value,
      },
    }
  }

  if (type === filterTypes[SWITCHER]) {
    return {
      booleanInput: {
        key: apiFilterName,
        value: value,
      },
    }
  }

  if (type === filterTypes[RRP_INTERVAL]) {
    return {
      complexInput: {
        key: apiFilterName,
        childrenInputs: [
          value?.currency && {
            listInput: {
              key: `${apiFilterName}.currency`,
              byName: value?.currency,
            },
          },

          value?.interval && {
            rangeInput: {
              key: `${apiFilterName}.value`,
              minValue: value?.interval?.minValue,
              maxValue: value?.interval?.maxValue,
            },
          },
        ].filter(Boolean),
      },
    }
  }

  if (type === filterTypes[SCORE]) {
    if (value === -1) return null
    return {
      listInput: {
        key: apiFilterName,
        byName: value,
      },
    }
  }

  return null
}

export const useMyWinesFiltersInputs = (storeName, filtersStorageName) => {
  const filtersFromStorage = ls.getObject(filtersStorageName)

  const filtersFromStore = useSelector(
    state => state?.tableFilters?.[storeName]?.filters
  )

  const getTableFilters = filters => {
    if (!filters) return null

    const inputs = []
    for (const item of getMyWinesFiltersCollection()) {
      const { type, filterName, apiFilterName } = item
      const inputItem = mapFilterForQuery(
        type,
        filters[filterName],
        apiFilterName
      )
      if (inputItem) inputs.push(inputItem)
    }

    return inputs.length ? inputs : null
  }

  return { getTableFilters, filtersFromStore, filtersFromStorage }
}

export const usePageData = () => {
  const [_loading, setLoading] = useState(true)
  const dispatch = useDispatch()
  const pageInfo = useSelector(getPageInfo) || {}
  const { page = 1 } = pageInfo
  const { sub } = useParams()
  const isShortlistPage = sub === 'shortlist'
  const prevSubRef = useRef(sub)
  const isSubChanged = prevSubRef.current !== sub
  const getVariables = useGetVariables()
  const { value: perPage } = usePerPage()
  const calculatePage = useCalculatePageOnPerPageChange()
  const companyCountryId = useAppSelector(getSelectedCompany)?.country?.id

  const { storeName, setPresetActionType, filtersStorageName } =
    useFilterStoreData()
  const filtersApplied = useSelector(state => {
    return state?.tableFilters?.[storeName]?.filtersApplied ?? false
  })
  const query = useSelector(state => {
    return state?.tableFilters?.[storeName]?.filters?.query ?? null
  })
  const presetInStore = useSelector(state => {
    return state?.tableFilters?.[storeName]?.preset ?? null
  })

  const { getTableFilters, filtersFromStorage, filtersFromStore } =
    useMyWinesFiltersInputs(storeName, filtersStorageName)

  const [
    getMyWines,
    {
      called: calledWines,
      loading: loadingWines,
      error: errorWines,
      data: dataWines,
    },
  ] = useLazyQuery(MyWinesDocument, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const [
    getMyWinesShortlist,
    {
      called: calledShortlist,
      loading: loadingShortlist,
      error: errorShortlist,
      data: dataShortlist,
    },
  ] = useLazyQuery(MyWinesShortlistDocument, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const called = isShortlistPage ? calledShortlist : calledWines
  const loading = isShortlistPage ? loadingShortlist : loadingWines
  const error = isShortlistPage ? errorShortlist : errorWines
  const data = isShortlistPage ? dataShortlist : dataWines

  const getTableData = (vars = {}) => {
    const variables = {
      variables: { ...(vars?.variables || {}), companyCountryId },
    }
    if (isShortlistPage) {
      getMyWinesShortlist(variables)
    } else {
      getMyWines(variables)
    }
  }

  const requestTableData = ({ page = 1, fromStore = false }) => {
    getTableData({
      variables: getVariables({
        page,
        facetsInputs: getTableFilters(
          fromStore ? filtersFromStore : filtersFromStorage
        ),
        query,
      }),
    })
  }

  const requestUpdateFilters = ({ filter = [] }) => {
    getTableData({
      variables: getVariables({
        page: 1,
        // sort,
        facetsInputs: filter,
        query,
      }),
    })
  }

  const filterQueryData = {
    getFiltersData: isShortlistPage
      ? ShortlistFiltersDocument
      : ProductsOrWinesFiltersDocument,
    getVariables: variables => getFilterVariables({ ...variables, query }),
    getSpecificFilterData: isShortlistPage
      ? ProductsShortlistFilterDocument
      : ProductsOrWinesFilterDocument,
  }

  useLayoutEffect(() => {
    dispatch(databaseReset())
  }, [])

  useEffect(() => {
    if (!data) return

    const productsOrWinesPagination = isShortlistPage
      ? data?.me?.company?.productsShortlistPagination
      : data?.me?.company?.producerTradeName?.productsOrWinesPagination
    const pageInfo = productsOrWinesPagination?.pageInfo || {}
    const items = productsOrWinesPagination?.items || []
    dispatch(setPageInfo(pageInfo))
    dispatch(setItems(items))
    setLoading(false)
  }, [data])

  useEffect(() => {
    setLoading(loading)
  }, [loading])

  useEffect(() => {
    requestTableData({})
  }, [filtersApplied])

  useEffect(() => {
    if (called) {
      if (query) requestTableData({ query, page })
      else requestTableData({ page })
    }
  }, [called, query, page])

  useEffect(() => {
    const page = calculatePage(pageInfo?.page)
    dispatch(setPageInfo({ ...pageInfo, page }))
    requestTableData({ page })
  }, [perPage])

  useEffect(() => {
    if (!called && !filtersApplied) {
      getTableData({
        variables: getVariables({ page: 1, sort: DEFAULT_SORT }),
      })
    }
    if (presetInStore && !filtersApplied) {
      dispatch({
        type: setPresetActionType,
        payload: null,
      })
    }
    return () => {
      dispatch(reset())
    }
  }, [])

  useEffect(() => {
    if (isSubChanged) {
      prevSubRef.current = sub
      dispatch(setPageInfo({ ...pageInfo, page: 1 }))
      requestTableData({ page: 1 })
    }
  }, [sub])

  return {
    showPreloader: !data || _loading,
    error,
    requestUpdateFilters,
    requestTableData,
    filterQueryData,
    page,
  }
}
